import { FileIcon, useDownloadFile } from '@newfront-insurance/admin-ui/files';
import type { novaToolDefs } from '@newfront-insurance/ai-api';
import { cn } from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import type { InferUITool, ToolUIPart } from 'ai';
import { Loader2 } from 'lucide-react';

import { getFileExtension, NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../../lib/analytics';

type ProcessFilesOutput = InferUITool<(typeof novaToolDefs)['processFiles']>['output'];

/**
 * Helper function to render generated files from tool output
 */
function renderGeneratedFiles(output: ProcessFilesOutput, messageUuid: string): React.ReactNode | null {
  if (typeof output === 'string') return null;

  // Use generated files from Files service
  if (output.generatedFiles && output.generatedFiles.length > 0) {
    const generatedFiles = output.generatedFiles.map((file) => ({
      uuid: file.fileUuid,
      filename: file.filename,
    }));
    return <GeneratedFilesList files={generatedFiles} messageUuid={messageUuid} />;
  }

  // Fallback to generatedFileUuids
  if (output.generatedFileUuids && output.generatedFileUuids.length > 0) {
    const generatedFiles = output.generatedFileUuids.map((uuid: string, idx: number) => ({
      uuid,
      filename: `Generated file ${idx + 1}`,
    }));
    return <GeneratedFilesList files={generatedFiles} messageUuid={messageUuid} />;
  }

  return null;
}

interface ProcessFilesToolProps {
  part: ToolUIPart;
  messageUuid: string;
}

/**
 * Component for rendering processFiles tool output.
 */
export function ProcessFilesTool({ part, messageUuid }: ProcessFilesToolProps) {
  if (part.state === 'output-available') {
    return renderGeneratedFiles(part.output as ProcessFilesOutput, messageUuid);
  }

  return null;
}

export interface GeneratedFileListItem {
  uuid: string | null;
  filename: string;
  isPending?: boolean;
}

export function GeneratedFilesList({
  files,
  messageUuid,
  source = 'generated_file',
}: {
  files: GeneratedFileListItem[];
  messageUuid?: string;
  source?: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-muted-foreground text-sm font-medium">Generated Files:</div>
      <div className="flex flex-row flex-wrap gap-2">
        {files.map((file) => (
          <ChatFileListItem
            key={`${file.filename}-${file.uuid ?? 'pending'}`}
            file={file}
            messageUuid={messageUuid}
            source={source}
          />
        ))}
      </div>
    </div>
  );
}

function ChatFileListItem({
  file,
  messageUuid,
  source,
}: {
  file: GeneratedFileListItem;
  messageUuid?: string;
  source: string;
}) {
  const { download, isDownloading } = useDownloadFile({});
  const analytics = useAnalytics();
  const isPending = file.isPending || !file.uuid;

  const handleClick = () => {
    if (file.uuid) {
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.CHAT_FILE_DOWNLOADED, {
        fileExtension: getFileExtension(file.filename),
        fileUuid: file.uuid,
        messageUuid,
        source,
      });
      download(file.uuid);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && file.uuid) {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-disabled={isPending || isDownloading}
      aria-busy={isPending || isDownloading}
      className={cn(
        'flex w-fit max-w-xs cursor-pointer items-center gap-2 rounded-sm px-1 py-1',
        'hover:bg-muted/50 transition-colors',
        {
          'pointer-events-none opacity-50': isDownloading || isPending,
          'animate-pulse': isPending,
        },
      )}
    >
      <FileIcon filename={file.filename} hasBackground={false} />
      <span className="text-steel-400 truncate text-xs">{file.filename}</span>
      {isPending && <Loader2 className="text-muted-foreground h-3.5 w-3.5 animate-spin" />}
    </div>
  );
}
